import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { captureAnalytics } from '../../../actions/preload';
import { hasFeatureInLocalStorage } from '../../../global/featureFlags';
import COLORS from '../../../util/constants/colors';
import { formatCount, percentOf } from './helpers';

export const SOURCE_META = {
  arm: { label: 'Agentic referral management', color: COLORS.PURPLE_SECONDARY_LITE },
  team: { label: 'Your team', color: COLORS.PURPLE_PRIMARY_LITE },
  automation: { label: 'Configured automations', color: COLORS.PURPLE_TERTIARY_LITE },
};

const BAR_COLORS = [COLORS.PURPLE_SECONDARY_LITE, '#4f8a8b', '#5f9e6e', '#b9975b'];

// Illustrative only: shown to orgs without ARM. Not measured from their data.
const ARM_EXAMPLE_TIMELINE = [
  { label: 'Referral arrives', seconds: 0 },
  { label: 'Referral processed', seconds: 15 },
  { label: 'First patient SMS sent', seconds: 30 },
];

const Card = ({ title, badge, children }) => (
  <div className="user-activity-card">
    <div className="user-activity-card-header">
      <span className="user-activity-eyebrow">{title}</span>
      {badge}
    </div>
    {children}
  </div>
);

Card.propTypes = {
  title: PropTypes.string.isRequired,
  badge: PropTypes.node,
  children: PropTypes.node.isRequired,
};

Card.defaultProps = {
  badge: null,
};

const Headline = ({ value, unit, caption }) => (
  <div className="user-activity-headline">
    <div>
      <span className="user-activity-headline-value">{value}</span>
      <span className="user-activity-headline-unit">{unit}</span>
    </div>
    <span className="user-activity-caption">{caption}</span>
  </div>
);

Headline.propTypes = {
  value: PropTypes.node.isRequired,
  unit: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
};

export const ProgressRow = ({
  label,
  count,
  max,
  color,
}) => (
  <div className="user-activity-progress-row">
    <div className="user-activity-progress-label">
      <span>{label}</span>
      <strong>{formatCount(count)}</strong>
    </div>
    <div className="user-activity-progress-track">
      <div style={{ width: `${percentOf(count, max)}%`, backgroundColor: color }} />
    </div>
  </div>
);

ProgressRow.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

const EmrCard = ({
  emrUploads,
  showArm,
  weeks,
  filtered,
}) => {
  const sources = Object.keys(SOURCE_META).filter((key) => key !== 'arm' || showArm);
  const trend = weeks.map((week) => ({ total: week.emr.team + week.emr.automation }));

  return (
    <Card title="Auto-uploaded to EMR">
      <Headline value={formatCount(emrUploads.total)} unit="completed" caption="In this date range" />
      <div className="user-activity-share-bar">
        {sources.map((key) => (
          <div
            key={key}
            style={{ flexGrow: emrUploads.by_source[key], backgroundColor: SOURCE_META[key].color }}
          />
        ))}
      </div>
      <div className="user-activity-legend-list">
        {sources.map((key) => (
          <div key={key} className="user-activity-legend-row">
            <span className="user-activity-dot" style={{ backgroundColor: SOURCE_META[key].color }} />
            <span className="user-activity-legend-label">{SOURCE_META[key].label}</span>
            <strong>{formatCount(emrUploads.by_source[key])}</strong>
            <span className="user-activity-caption">{`${percentOf(emrUploads.by_source[key], emrUploads.total)}%`}</span>
          </div>
        ))}
      </div>
      {filtered && (
        <span className="user-activity-footnote">Agent and automation counts are organization-wide</span>
      )}
      {/* One week is one point: no trend to draw. */}
      {trend.length > 1 && (
        <div className="user-activity-mini-trend">
          <span className="user-activity-caption">Team + automation</span>
          <ResponsiveContainer width="100%" height={48}>
            <AreaChart data={trend}>
              <Area
                type="monotone"
                dataKey="total"
                stroke={COLORS.PURPLE_SECONDARY_LITE}
                fill={COLORS.PURPLE_QUARTERNARY_LITE}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

EmrCard.propTypes = {
  emrUploads: PropTypes.object.isRequired,
  showArm: PropTypes.bool.isRequired,
  weeks: PropTypes.array.isRequired,
  filtered: PropTypes.bool.isRequired,
};

const StatusChangesCard = ({ statusChanges }) => {
  const max = Math.max(0, ...statusChanges.by_status.map((row) => row.count));

  return (
    <Card title="Referral status changes">
      <Headline value={formatCount(statusChanges.total)} unit="by your team" caption="In this date range · moved to" />
      <div className="user-activity-progress-list user-activity-status-list">
        {statusChanges.by_status.length === 0 && (
          <span className="user-activity-caption">No status changes in this date range</span>
        )}
        {statusChanges.by_status.map((row, index) => (
          <ProgressRow
            key={row.status_id}
            label={row.status}
            count={row.count}
            max={max}
            color={BAR_COLORS[index % BAR_COLORS.length]}
          />
        ))}
      </div>
    </Card>
  );
};

StatusChangesCard.propTypes = {
  statusChanges: PropTypes.object.isRequired,
};

const ArmTeaser = () => {
  const total = ARM_EXAMPLE_TIMELINE[ARM_EXAMPLE_TIMELINE.length - 1].seconds;
  // /app/arm redirects home without the upsell flag, so only link when it would land.
  const canSeeArmUpsell = hasFeatureInLocalStorage('arm_upsell_flag');

  return (
    <Card title="Agentic referral management" badge={<span className="user-activity-badge">Not enabled</span>}>
      <div className="user-activity-headline">
        <div>
          <span className="user-activity-headline-value">{total}</span>
          <span className="user-activity-headline-unit">sec</span>
        </div>
        <strong>From referral arrival to first SMS</strong>
        <span className="user-activity-caption">Process the referral and start patient outreach in seconds.</span>
      </div>
      <div className="user-activity-timeline">
        <span className="user-activity-footnote">Illustrative workflow</span>
        {ARM_EXAMPLE_TIMELINE.map((step) => (
          <div key={step.label} className="user-activity-timeline-step">
            <span className="user-activity-dot" style={{ backgroundColor: COLORS.PURPLE_SECONDARY_LITE }} />
            <span className="user-activity-legend-label">{step.label}</span>
            <span className="user-activity-caption">{`${step.seconds} sec`}</span>
          </div>
        ))}
      </div>
      {canSeeArmUpsell && (
        <Link className="user-activity-cta" to="/app/arm" onClick={() => captureAnalytics('user_activity_arm_link_clicked')}>
          See how it works
          <ArrowRight size={14} />
        </Link>
      )}
      <span className="user-activity-footnote">Example timing · actual processing times vary</span>
    </Card>
  );
};

const ArmCard = ({ arm }) => {
  // No figures means the org never had an agent. One that turned ARM off keeps its history.
  if (arm.referrals_worked === undefined) return <ArmTeaser />;

  const rows = [
    { label: 'New referrals processed', count: arm.new_referrals_processed },
    { label: 'Patients sent SMS', count: arm.patients_sent_sms },
    { label: 'Status changes', count: arm.status_changes },
    arm.moved_to_contacted && { label: `Moved to ${arm.moved_to_contacted.status}`, count: arm.moved_to_contacted.count },
    arm.referrals_scheduled && { label: 'Referrals scheduled', count: arm.referrals_scheduled.count },
  ].filter(Boolean);
  const max = Math.max(arm.referrals_worked, ...rows.map((row) => row.count));

  return (
    <Card
      title="Agentic referral management"
      badge={arm.enabled
        ? <span className="user-activity-badge enabled">Enabled</span>
        : <span className="user-activity-badge">Not enabled</span>}
    >
      <Headline
        value={formatCount(arm.referrals_worked)}
        unit="referrals worked"
        caption="Organization-wide · in this date range"
      />
      <div className="user-activity-progress-list">
        {rows.map((row, index) => (
          <ProgressRow
            key={row.label}
            label={row.label}
            count={row.count}
            max={max}
            color={BAR_COLORS[index % BAR_COLORS.length]}
          />
        ))}
      </div>
      <span className="user-activity-footnote">Outcomes can overlap · agent work only</span>
    </Card>
  );
};

ArmCard.propTypes = {
  arm: PropTypes.object.isRequired,
};

// ARM work stays visible after the agent is turned off: it still happened.
export const showArmWork = (data) => data.arm.enabled || data.emr_uploads.by_source.arm > 0;

const SummaryCards = ({ data }) => (
  <div className="user-activity-summary">
    <EmrCard
      emrUploads={data.emr_uploads}
      showArm={showArmWork(data)}
      weeks={data.work_over_time}
      filtered={data.filtered}
    />
    <StatusChangesCard statusChanges={data.status_changes} />
    <ArmCard arm={data.arm} />
  </div>
);

SummaryCards.propTypes = {
  data: PropTypes.object.isRequired,
};

export default SummaryCards;
